<template>
  <ModalComponent
    :submitButtonText="confirmButtonText"
    :hideSubmitButton="true"
    :noHeader="noHeader"
    :dismissible="!busy"
    @setForceToggle="setForceToggle"
    @close="handleCancel"
    size="lg"
  >
    <template v-if="!noHeader" #title>
      <slot name="title">{{ title }}</slot>
    </template>

    <template #body>
      <!--
        When a consumer provides a `#body` slot, we hand them the full body
        surface (no wrapping padding) so modals with custom layouts (icons +
        panels + multi-row chrome) can own the spacing. The default rendering
        keeps the previous simple `<p>message</p>` + extras contract so
        existing call sites don't need to migrate.
      -->
      <slot name="body">
        <div class="p-3">
          <p
            class="mb-0"
            :style="preserveWhitespace ? 'white-space: pre-line' : ''"
          >
            {{ message }}
          </p>
          <slot></slot>
        </div>
      </slot>
      <!-- Outside the fallback: a consumer that owns the body still needs the failure shown here. -->
      <p v-if="errorMessage" class="error-note m-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ errorMessage }}</span>
      </p>
    </template>

    <template #footer>
      <div v-if="$slots.footerStart" class="me-auto">
        <slot name="footerStart" />
      </div>
      <button
        type="button"
        class="btn"
        :class="cancelButtonClass"
        :disabled="busy"
        @click="handleCancel"
      >
        {{ t("common.cancel") }}
      </button>
      <button
        type="button"
        class="btn"
        :class="confirmButtonClass"
        :disabled="busy"
        @click="handleConfirm"
      >
        <FontAwesomeIcon
          v-if="confirmButtonIcon"
          :icon="confirmButtonIcon"
          class="me-2"
        />
        {{ confirmButtonText }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
  visible: boolean;
  title?: string;
  message?: string;
  confirmButtonText?: string;
  confirmButtonClass?: string;
  cancelButtonClass?: string;
  confirmButtonIcon?: string | string[];
  preserveWhitespace?: boolean;
  /** Skip the modal-header; the body slot owns all top chrome. */
  noHeader?: boolean;
  /**
   * Keep the dialog open after `confirm` so the parent can run the action and show its failure
   * here through `errorMessage`; the parent then closes it by dropping `visible`.
   */
  closeOnConfirm?: boolean;
  busy?: boolean;
  errorMessage?: string | null;
}

// `defineProps` defaults can't reference locals (they're hoisted out of
// setup), so text/class fallbacks stay as computeds that read the raw prop.
const props = withDefaults(defineProps<Props>(), {
  title: "",
  message: "",
  confirmButtonClass: "btn-primary",
  cancelButtonClass: "btn-link text-secondary",
  noHeader: false,
  closeOnConfirm: true,
  busy: false,
  errorMessage: null,
});

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const { t } = useI18n();

const confirmButtonText = computed(
  () => props.confirmButtonText ?? t("common.confirm")
);

const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
  fn(props.visible);
};

watch(
  () => props.visible,
  (value) => {
    forceToggle.value?.(value);
  }
);

const handleConfirm = () => {
  if (props.closeOnConfirm) forceToggle.value?.(false);
  emit("confirm");
};

const handleCancel = () => {
  forceToggle.value?.(false);
  emit("cancel");
};
</script>

<style lang="scss" scoped></style>
